import { FormProps } from 'types/form';
import { Website } from '../types/dtos';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import OWForm from 'components/layout/ow-form';
import { Autocomplete, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { ResponseProductDto } from 'features/product/types/dtos';
import { ResponseCompanyDto } from 'features/company/types/dtos';
import FormFooter from 'components/layout/form-footer';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'utils/object-helpers';

export type WebsiteFormProps = FormProps<Website> & {
    products?: { content: ResponseProductDto[] };
    companies?: { content: ResponseCompanyDto[] };
    isAdmin?: boolean;
};

type WebsiteFormType = (props: WebsiteFormProps) => ReactNode;

export const WebsiteForm: WebsiteFormType = ({
    formData: website,
    companies,
    onChange,
    onCancel,
    onSubmit,
    isSaveLoading,
    errors,
    disableSubmit,
    isAdmin = true,
}) => {
    const { t } = useTranslation();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('WEBSITE_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={2}>
                    <TextField
                        value={website.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('WEBSITE_FORM.PLACEHOLDER_NAME')}
                        label={t('WEBSITE_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                    />
                    <TextField
                        value={website.domain}
                        onChange={(e) => onChange('domain', e.target.value)}
                        placeholder={t('WEBSITE_FORM.PLACEHOLDER_DOMAIN')}
                        label={t('WEBSITE_FORM.LABEL_DOMAIN')}
                        required
                        error={!!errors?.domain}
                        helperText={errors?.domain?.errorMessage}
                        disabled={!isAdmin}
                    />
                </Stack>
            </Stack>
            <Divider variant="middle" />
            <Stack spacing={2}>
                <Autocomplete
                    options={companies?.content || []}
                    value={companies?.content.find((company) => company.id === website.companyId) || null}
                    getOptionLabel={(option: { id: string; name: string }) => option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('WEBSITE_FORM.LABEL_COMPANY')}
                            required
                            error={!!errors?.companyId}
                            helperText={errors?.companyId?.errorMessage}
                        />
                    )}
                    onChange={(_, value) => onChange('companyId', value?.id)}
                    hidden={!isAdmin}
                />
                <TextField
                    value={website.description}
                    onChange={(e) => onChange('description', e.target.value)}
                    placeholder={t('WEBSITE_FORM.PLACEHOLDER_DESCRIPTION')}
                    label={t('WEBSITE_FORM.LABEL_DESCRIPTION')}
                    type="text"
                    error={!!errors?.description}
                    helperText={errors?.description?.errorMessage}
                    multiline
                    minRows={3}
                />
            </Stack>
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('WEBSITE_FORM.CANCEL')}
                    </Button>
                    <LoadingButton
                        loading={isSaveLoading}
                        type="submit"
                        disabled={!isEmpty(errors) || disableSubmit}
                        data-testid="submit-button"
                    >
                        {t('WEBSITE_FORM.SUBMIT')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
